import { Disposable, type Logger } from '@ts-ssh2/common';
import type { SshTransportProtocol } from './protocol/transport-protocol.js';
import type { Connection } from './connection.js';
import { SSH_DISCONNECT_BY_APPLICATION } from './protocol/constants.js';

/**
 * Configuration for the keepalive manager.
 */
export type KeepaliveConfig = {
	/** Interval between probes in milliseconds. Must be > 0. */
	intervalMs: number;
	/** Maximum time to wait for a probe reply before disconnecting, in milliseconds. */
	timeoutMs: number;
};

/**
 * Manages SSH keepalive probing over the lifetime of a connection.
 *
 * Selects the best mechanism (OpenSSH transport ping when advertised,
 * otherwise `keepalive@openssh.com` global request), sends probes at
 * a fixed interval, and forcibly disconnects when the peer stops
 * responding.
 *
 * Only one probe is ever in flight at a time. On each interval tick,
 * if no probe is outstanding a new one is sent and a deadline timer
 * is started. If the reply arrives, the deadline is cleared and the
 * next probe will be sent on the following interval tick. If the
 * deadline fires first, the connection is forcibly closed.
 *
 * Extends Disposable — disposing stops all timers and unsubscribes
 * from transport events. Idempotent via the Disposable base class.
 */
export class KeepaliveManager extends Disposable {
	private intervalTimer: ReturnType<typeof setInterval> | null = null;
	private deadlineTimer: ReturnType<typeof setTimeout> | null = null;
	private probeInFlight = false;
	private disconnectSub: Disposable | null = null;

	readonly mechanism: string;
	private readonly sendProbe: () => Promise<void>;

	constructor(
		private readonly transport: SshTransportProtocol,
		connection: Connection,
		private readonly config: KeepaliveConfig,
		private readonly logger: Logger,
	) {
		super();

		// select mechanism
		if (transport.supportsOpenSshPing) {
			this.mechanism = 'openssh transport ping';
			this.sendProbe = () => transport.sendPing();
		} else {
			this.mechanism = 'keepalive@openssh.com global request';
			this.sendProbe = () => connection.sendKeepaliveRequest();
		}
	}

	/**
	 * Start the keepalive loop.
	 * Must be called once after construction; calling again is a no-op.
	 */
	start(): void {
		if (this.intervalTimer !== null) return;

		this.logger.debug(
			`keepalive enabled: interval=${this.config.intervalMs}ms, timeout=${this.config.timeoutMs}ms, mechanism=${this.mechanism}`,
		);

		this.intervalTimer = setInterval(() => {
			this.tick();
		}, this.config.intervalMs);

		// stop on transport disconnect
		this.disconnectSub = this.transport.onDisconnect.subscribe(() => {
			this.dispose();
		});
	}

	override dispose(): void {
		if (this.disposed) return;

		if (this.intervalTimer !== null) {
			clearInterval(this.intervalTimer);
			this.intervalTimer = null;
		}

		if (this.deadlineTimer !== null) {
			clearTimeout(this.deadlineTimer);
			this.deadlineTimer = null;
		}

		this.logger.debug('keepalive stopped');

		this.disconnectSub?.dispose();
		this.disconnectSub = null;

		super.dispose();
	}

	// ── internals ──────────────────────────────────────────────────

	private tick(): void {
		if (this.probeInFlight) {
			// previous probe still outstanding — deadline timer will handle timeout
			return;
		}

		this.probeInFlight = true;
		this.logger.debug('keepalive probe sent');

		// start a deadline — if no reply within timeoutMs, disconnect
		this.deadlineTimer = setTimeout(() => {
			this.deadlineTimer = null;
			this.logger.debug(
				`keepalive timeout: no reply within ${this.config.timeoutMs}ms, forcing disconnect`,
			);
			this.handleTimeout();
		}, this.config.timeoutMs);

		this.sendProbe()
			.then(() => {
				if (this.disposed) return;
				this.probeInFlight = false;
				if (this.deadlineTimer !== null) {
					clearTimeout(this.deadlineTimer);
					this.deadlineTimer = null;
				}
				this.logger.debug('keepalive reply received');
			})
			.catch(() => {
				// transport closed, cleanup will happen via dispose
			});
	}

	private handleTimeout(): void {
		// attempt graceful disconnect (best-effort)
		this.transport.disconnect(SSH_DISCONNECT_BY_APPLICATION, 'keepalive timeout').catch(() => {});

		this.dispose();
	}
}
